import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '@/redux/store/configureStore'
import { fetchVisaDenialEligibility, isVisaDenialAvailable, VisaDenialPayload, VisaDenialResponse } from '@/utils/homepage/visaDenialApi'

interface VisaDenialState {
    isLoading: boolean
    isAvailable: boolean
    error: boolean
    data: VisaDenialResponse | null
}

const initialState: VisaDenialState = {
    isLoading: false,
    isAvailable: false,
    error: false,
    data: null
}

/**
 * Async thunk to fetch visa denial eligibility
 */
export const fetchVisaDenialEligibilityData = createAsyncThunk(
    'visaDenial/eligibility',
    async (payload: VisaDenialPayload) => {
        const response = await fetchVisaDenialEligibility(payload)
        return response
    }
)

export const visaDenialSlice = createSlice({
    name: 'visaDenial',
    initialState,
    reducers: {
        resetVisaDenialState: (state) => {
            state.isLoading = false
            state.isAvailable = false
            state.error = false
            state.data = null
        },
        setVisaDenialAvailable: (state, action: PayloadAction<boolean>) => {
            state.isAvailable = action.payload
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVisaDenialEligibilityData.pending, (state) => {
                state.isLoading = true
                state.error = false
            })
            .addCase(fetchVisaDenialEligibilityData.fulfilled, (state, action) => {
                state.isLoading = false
                state.error = false
                const response = action.payload as VisaDenialResponse
                state.data = response
                
                // Parse response to check if visa denial is available
                // isVisaDenialAvailable handles both response structures
                state.isAvailable = isVisaDenialAvailable(response)
            })
            .addCase(fetchVisaDenialEligibilityData.rejected, (state) => {
                state.isLoading = false
                state.error = true
                state.isAvailable = false
                state.data = null
            })
    }
})

const { reducer, actions } = visaDenialSlice

export const { resetVisaDenialState, setVisaDenialAvailable } = actions

// Selectors
export const getVisaDenialState = (state: RootState) => state.visaDenial
export const getVisaDenialAvailable = (state: RootState) => state.visaDenial.isAvailable
export const getVisaDenialLoading = (state: RootState) => state.visaDenial.isLoading
export const getVisaDenialError = (state: RootState) => state.visaDenial.error
export const getVisaDenialData = (state: RootState) => state.visaDenial.data

export default reducer

